/**
 * Draft privacy policy and terms (ichegoubr.com/privacidade, /termos). The apps' "Política de
 * privacidade" (PrivacySummary.policyUrl) opens the first one. Every number here mirrors remote
 * config or docs/architecture.md (retention); keep them in step. Nothing here is reviewed yet.
 */
export const DRAFT_NOTICE = "Rascunho — revisão jurídica pendente";

export type LegalSection = { id: string; heading: string; paragraphs: string[]; items?: string[] };
export type LegalDoc = { title: string; updatedAt: string; intro: string; sections: LegalSection[] };

/** Placeholders until the company is registered. */
export const COMPANY = {
  name: "ichegou Tecnologia Ltda. (razão social a definir)",
  cnpj: "00.000.000/0001-00 (a definir)",
  privacyEmail: "privacidade@ichegoubr.com",
  supportEmail: "ajuda@ichegoubr.com",
};

export const PRIVACY: LegalDoc = {
  title: "Política de Privacidade",
  updatedAt: "2026-11-02",
  intro:
    "Esta política explica quais dados pessoais o ichegou trata quando você pede uma corrida, faz um envio ou dirige com a gente, por que tratamos, com quem compartilhamos, por quanto tempo guardamos e como você exerce seus direitos pela Lei Geral de Proteção de Dados (Lei nº 13.709/2018, LGPD).",
  sections: [
    {
      id: "controlador",
      heading: "1. Quem é o controlador",
      paragraphs: [
        `O controlador dos dados é ${COMPANY.name}, CNPJ ${COMPANY.cnpj}. O encarregado pelo tratamento de dados pessoais (DPO) atende pelo e-mail ${COMPANY.privacyEmail}.`,
      ],
    },
    {
      id: "dados",
      heading: "2. Quais dados tratamos",
      paragraphs: ["Tratamos apenas o necessário para o serviço funcionar com segurança:"],
      items: [
        "Cadastro: nome, telefone, e-mail, CPF (quando exigido para pagamento ou repasse) e foto, se você enviar.",
        "Motoristas: CNH, documentos e dados do veículo, chave Pix para repasses e informações da análise de cadastro.",
        "Localização: o ponto de partida e o destino que você informa e, durante a corrida, a posição do motorista enviada pelo app a cada poucos segundos.",
        "Corridas e envios: trajeto, horários, categoria, valores, avaliações, cancelamentos e, nos envios, fotos de coleta e entrega e o telefone de quem recebe.",
        "Pagamentos: dados da transação Pix ou do cartão processados pelo Mercado Pago. O número do cartão vai direto do app para o Mercado Pago; o ichegou nunca o recebe.",
        "Comunicações: mensagens do chat da corrida, conversas com o suporte e contatos de confiança que você cadastrar.",
        "Áudio da corrida (opcional): só quando você liga \"Gravar áudio das corridas\". A gravação é criptografada no aparelho e só é enviada se você anexá-la a um alerta de emergência ou relato de incidente.",
        "Aparelho: modelo, sistema, versão do app e tokens de notificação, para enviar avisos e investigar falhas.",
      ],
    },
    {
      id: "finalidades",
      heading: "3. Para que usamos e com qual base legal",
      paragraphs: [],
      items: [
        "Prestar o serviço (encontrar motorista, calcular o preço, acompanhar a corrida, pagar e repassar): execução de contrato (art. 7º, V).",
        "Segurança de passageiros e motoristas (código de embarque, compartilhamento de viagem, central de segurança, prevenção a fraudes): legítimo interesse (art. 7º, IX) e proteção da vida (art. 7º, VII).",
        "Obrigações fiscais, contábeis e regulatórias, inclusive a guarda de registros de corridas e pagamentos: cumprimento de obrigação legal (art. 7º, II).",
        "Gravação de áudio e recibos por e-mail: consentimento (art. 7º, I), que você pode retirar a qualquer momento nas configurações do app.",
        "Lembrar por e-mail quem começou o cadastro de motorista no site e não terminou (no máximo dois lembretes, com link para parar de receber): legítimo interesse.",
        "Melhorar o app e medir a qualidade do serviço com dados agregados: legítimo interesse.",
      ],
    },
    {
      id: "compartilhamento",
      heading: "4. Com quem compartilhamos",
      paragraphs: ["Não vendemos dados pessoais. Compartilhamos o mínimo necessário com:"],
      items: [
        "O outro lado da corrida: o motorista vê seu primeiro nome, o ponto de partida e o destino; você vê o primeiro nome, a foto, a avaliação, o veículo e a placa do motorista.",
        "Quem você escolher: contatos de confiança e pessoas com o link da viagem compartilhada veem o trajeto ao vivo até a corrida terminar.",
        "Prestadores de serviço que tratam dados em nosso nome: Mercado Pago (pagamentos e repasses), Twilio (SMS e WhatsApp), Google (mapas, rotas e endereços), Apple e Google (notificações), Cloudflare (armazenamento de arquivos e e-mail) e Hetzner (servidores).",
        "Autoridades públicas, quando houver obrigação legal ou ordem judicial.",
      ],
    },
    {
      id: "transferencia",
      heading: "5. Transferência internacional",
      paragraphs: [
        "Nossos servidores ficam nos Estados Unidos (Hetzner, Ashburn) e alguns prestadores tratam dados fora do Brasil. Essas transferências seguem o art. 33 da LGPD, com as cláusulas-padrão contratuais aprovadas pela ANPD (Resolução CD/ANPD nº 19/2024) e os acordos de tratamento de dados de cada prestador.",
      ],
    },
    {
      id: "retencao",
      heading: "6. Por quanto tempo guardamos",
      paragraphs: [],
      items: [
        "Posições do motorista durante a corrida: 30 dias.",
        "E-mail informado ao iniciar o cadastro de motorista no site: 90 dias depois da última atividade.",
        "Mensagens do chat da corrida: 180 dias, criptografadas; depois são apagadas.",
        "Gravações de áudio enviadas: 180 dias. As que ficam só no aparelho são apagadas em 7 dias.",
        "Corridas, pagamentos e repasses: 5 anos, por obrigação fiscal.",
        "Registros de acesso ao app: 6 meses (Marco Civil da Internet, art. 15).",
      ],
    },
    {
      id: "direitos",
      heading: "7. Seus direitos",
      paragraphs: [
        "Você pode pedir confirmação do tratamento, acesso, correção, anonimização, portabilidade, informação sobre compartilhamentos e revogação do consentimento (art. 18 da LGPD). No app, Conta → Privacidade mostra um resumo dos seus dados. Para o restante, escreva para " +
          COMPANY.privacyEmail +
          ". Você também pode reclamar à Autoridade Nacional de Proteção de Dados (ANPD).",
      ],
    },
    {
      id: "exclusao",
      heading: "8. Exclusão da conta",
      paragraphs: [
        "Peça a exclusão em Conta → Privacidade → Excluir conta. Você tem 7 dias para desistir. Depois disso, seu nome, telefone, e-mail, CPF, fotos, documentos, endereços salvos, contatos e gravações são apagados ou anonimizados. Guardamos, sem identificar você, apenas o que a lei exige: corridas, pagamentos e repasses.",
        "A exclusão fica pendente enquanto houver uma corrida ativa ou agendada, um pagamento em aberto ou, para motoristas, saldo a receber.",
      ],
    },
    {
      id: "seguranca",
      heading: "9. Segurança",
      paragraphs: [
        "Usamos conexões criptografadas, criptografia das mensagens do chat e das gravações de áudio, acesso da equipe limitado por função, verificação em duas etapas para a equipe e registro de todo acesso a dados pessoais.",
      ],
    },
    {
      id: "menores",
      heading: "10. Menores de idade",
      paragraphs: ["A conta é para maiores de 18 anos. Menores podem viajar acompanhados de um adulto responsável pela conta."],
    },
    {
      id: "alteracoes",
      heading: "11. Alterações",
      paragraphs: ["Avisaremos no app e por e-mail antes de mudanças relevantes nesta política."],
    },
  ],
};

export const TERMS: LegalDoc = {
  title: "Termos de Uso",
  updatedAt: "2026-10-19",
  intro:
    "Estes termos regem o uso dos apps e do site do ichegou por passageiros, remetentes de envios e motoristas parceiros. Ao criar uma conta você concorda com eles e com a Política de Privacidade.",
  sections: [
    {
      id: "servico",
      heading: "1. O serviço",
      paragraphs: [
        "O ichegou é uma plataforma de tecnologia que conecta passageiros e remetentes a motoristas parceiros independentes para corridas e envios de pacotes. Os motoristas não são empregados do ichegou.",
      ],
    },
    {
      id: "conta",
      heading: "2. Sua conta",
      paragraphs: ["Você precisa ter 18 anos ou mais, informar dados verdadeiros e manter o acesso ao seu telefone. A conta é pessoal e intransferível."],
    },
    {
      id: "precos",
      heading: "3. Preços e pagamento",
      items: [
        "O preço é mostrado e fechado antes do pedido, incluindo a tarifa dinâmica quando houver.",
        "No Pix, o pagamento é feito antes de procurarmos o motorista. No cartão, disponível depois de algumas corridas pagas, a cobrança é feita ao fim da corrida.",
        "Cupons têm regras próprias, informadas no app. A taxa pet e as gorjetas vão integralmente para o motorista.",
        "Ajustes depois da corrida são feitos apenas por reembolso.",
      ],
      paragraphs: [],
    },
    {
      id: "cancelamento",
      heading: "4. Cancelamentos",
      paragraphs: [
        "Cancelar é gratuito logo após o aceite do motorista e quando ele está muito atrasado em relação ao tempo prometido. Fora disso, pode haver uma taxa de cancelamento, informada no app antes de você confirmar, paga ao motorista; o restante é reembolsado.",
      ],
    },
    {
      id: "conduta",
      heading: "5. Conduta",
      items: [
        "Trate todos com respeito. Discriminação, assédio e violência levam ao bloqueio da conta.",
        "Use o código de embarque e confira o veículo e a placa antes de entrar.",
        "Envios não podem conter itens proibidos por lei, perigosos, perecíveis sem embalagem adequada, dinheiro ou documentos de terceiros.",
      ],
      paragraphs: [],
    },
    {
      id: "motoristas",
      heading: "6. Motoristas parceiros",
      paragraphs: [
        "O cadastro passa por análise de documentos. Os ganhos de cada corrida aparecem no app; o repasse é semanal por Pix, e o saque na hora pode ser pedido no app. O ichegou pode suspender o parceiro que descumprir estes termos, com direito a defesa pelo suporte.",
      ],
    },
    {
      id: "seguranca",
      heading: "7. Segurança",
      paragraphs: [
        "Em perigo, ligue 190. O botão Emergência do app também avisa a central do ichegou e seus contatos de confiança. Esses recursos complementam, e não substituem, os serviços públicos de emergência.",
      ],
    },
    {
      id: "responsabilidade",
      heading: "8. Responsabilidade",
      paragraphs: ["Nos limites do Código de Defesa do Consumidor, o ichegou responde pelo funcionamento da plataforma e pelo atendimento do suporte."],
    },
    {
      id: "foro",
      heading: "9. Lei e foro",
      paragraphs: [`Aplicam-se as leis do Brasil. Fica eleito o foro do domicílio do consumidor. Dúvidas: ${COMPANY.supportEmail}.`],
    },
  ],
};
